import { api } from "@Aspecto/backend/convex/_generated/api";
import { useUser } from "@clerk/tanstack-react-start";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Camera, Eye, Loader2, Mail, MapPin, Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { AppPageContent } from "@/components/layout/app-page";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { NavigationGuard } from "@/components/ui/navigation-guard";
import { PageHeader } from "@/components/ui/page-header";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/app/profile/$userId")({
  component: ProfilePage,
});

function ProfilePage() {
  const { userId } = Route.useParams();
  const { data: profile, isLoading } = useQuery(
    convexQuery(api.users.getUserProfile, { userId: userId as any }),
  );

  if (isLoading) {
    return (
      <AppPageContent>
        <div className="w-full max-w-2xl animate-pulse space-y-6">
          <div className="flex items-center gap-5">
            <div className="h-16 w-16 rounded-full bg-muted" />
            <div className="space-y-2">
              <div className="h-5 w-36 rounded bg-muted" />
              <div className="h-4 w-48 rounded bg-muted" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="h-10 w-full rounded-lg bg-muted" />
            <div className="h-10 w-full rounded-lg bg-muted" />
            <div className="h-20 w-full rounded-lg bg-muted" />
          </div>
        </div>
      </AppPageContent>
    );
  }

  if (!profile) {
    return (
      <AppPageContent>
        <div className="flex w-full max-w-2xl flex-col py-20">
          <p className="text-lg font-medium text-foreground">Profile not found</p>
          <p className="mt-1 text-sm text-muted-foreground">
            This profile doesn't exist or you don't have access to view it.
          </p>
        </div>
      </AppPageContent>
    );
  }

  if (profile.isOwnProfile) {
    return <OwnProfileForm profile={profile} />;
  }

  return (
    <AppPageContent>
      <div className="w-full max-w-2xl">
        <OtherProfileCard profile={profile} />
      </div>
    </AppPageContent>
  );
}

interface ProfileData {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
  jobTitle?: string;
  department?: string;
  location?: string;
  bio?: string;
  isOwnProfile: boolean;
}

function OwnProfileForm({ profile }: { profile: ProfileData }) {
  const { user } = useUser();
  const updateProfile = useMutation(api.users.updateProfile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [location, setLocation] = useState("");
  const [bio, setBio] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setFirstName(profile.firstName || "");
    setLastName(profile.lastName || "");
    setJobTitle(profile.jobTitle || "");
    setDepartment(profile.department || "");
    setLocation(profile.location || "");
    setBio(profile.bio || "");
  }, [profile]);

  const displayName =
    profile.firstName && profile.lastName
      ? `${profile.firstName} ${profile.lastName}`
      : profile.firstName || profile.email;

  const initials =
    profile.firstName && profile.lastName
      ? `${profile.firstName[0]}${profile.lastName[0]}`
      : profile.firstName?.[0] || profile.email[0]?.toUpperCase() || "?";

  const isDirty =
    jobTitle !== (profile.jobTitle || "") ||
    department !== (profile.department || "") ||
    location !== (profile.location || "") ||
    bio !== (profile.bio || "");

  if (previewing) {
    const previewProfile: ProfileData = {
      ...profile,
      firstName: firstName.trim() || profile.firstName,
      lastName: lastName.trim() || profile.lastName,
      jobTitle: jobTitle.trim() || undefined,
      department: department.trim() || undefined,
      location: location.trim() || undefined,
      bio: bio.trim() || undefined,
      isOwnProfile: false,
    };

    return (
      <AppPageContent>
        <div className="mb-4 flex w-full max-w-2xl items-center justify-between">
          <p className="text-sm text-muted-foreground">This is how others see your profile.</p>
          <Button variant="outline" size="sm" onClick={() => setPreviewing(false)}>
            <Pencil className="h-3.5 w-3.5" />
            Back to editing
          </Button>
        </div>
        <OtherProfileCard profile={previewProfile} />
      </AppPageContent>
    );
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First name and last name are required");
      return;
    }

    setIsSubmitting(true);
    try {
      await Promise.all([
        updateProfile({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          jobTitle: jobTitle.trim() || undefined,
          department: department.trim() || undefined,
          location: location.trim() || undefined,
          bio: bio.trim() || undefined,
        }),
        user?.update({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        }),
      ]);
      toast.success("Profile updated successfully!");
    } catch {
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppPageContent>
      <div className="w-full max-w-2xl">
        <NavigationGuard isDirty={isDirty} />
        <PageHeader
          title="Profile"
          description="Your profile information visible to your organization."
        />
        <form onSubmit={handleSave}>
          <SectionCard>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={isUploadingImage}
                    render={
                      <button
                        type="button"
                        className="relative group shrink-0 cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    }
                  >
                    <Avatar className="h-14 w-14 text-lg transition-opacity group-hover:opacity-80">
                      {(localImageUrl || profile.imageUrl) && (
                        <AvatarImage src={localImageUrl || profile.imageUrl} alt={displayName} />
                      )}
                      <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                    </Avatar>
                    {isUploadingImage ? (
                      <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-card ring-2 ring-card">
                        <Loader2 className="h-2.5 w-2.5 animate-spin text-muted-foreground" />
                      </span>
                    ) : (
                      <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-card ring-2 ring-card transition-colors group-hover:bg-accent">
                        <Camera className="h-2.5 w-2.5 text-muted-foreground" />
                      </span>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                      <Camera className="h-4 w-4" />
                      Upload photo
                    </DropdownMenuItem>
                    {profile.imageUrl && (
                      <DropdownMenuItem
                        onClick={async () => {
                          if (!user) return;
                          setIsUploadingImage(true);
                          setLocalImageUrl(null);
                          try {
                            await user.setProfileImage({ file: null });
                            toast.success("Profile photo removed.");
                          } catch {
                            toast.error("Failed to remove photo.");
                          } finally {
                            setIsUploadingImage(false);
                          }
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove photo
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !user) return;
                    const previewUrl = URL.createObjectURL(file);
                    setLocalImageUrl(previewUrl);
                    setIsUploadingImage(true);
                    try {
                      await user.setProfileImage({ file });
                      toast.success("Profile photo updated!");
                    } catch {
                      toast.error("Failed to upload photo. Please try again.");
                      setLocalImageUrl(null);
                    } finally {
                      setIsUploadingImage(false);
                      e.target.value = "";
                    }
                  }}
                />
                <div className="min-w-0">
                  <p className="text-base font-semibold text-foreground">{displayName}</p>
                  <p className="text-sm text-muted-foreground">{profile.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="jobTitle">Job title</Label>
                <Input
                  id="jobTitle"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Software Engineer"
                  disabled={isSubmitting}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="e.g. Engineering"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. London, UK"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell your colleagues a bit about yourself..."
                  disabled={isSubmitting}
                  rows={3}
                />
              </div>
            </div>
          </SectionCard>

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setPreviewing(true)}>
              <Eye className="h-3.5 w-3.5" />
              Preview
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
    </AppPageContent>
  );
}

function OtherProfileCard({ profile }: { profile: ProfileData }) {
  const displayName =
    profile.firstName && profile.lastName
      ? `${profile.firstName} ${profile.lastName}`
      : profile.firstName || profile.email;

  const initials =
    profile.firstName && profile.lastName
      ? `${profile.firstName[0]}${profile.lastName[0]}`
      : profile.firstName?.[0] || profile.email[0]?.toUpperCase() || "?";

  const hasAbout = profile.department || profile.location || profile.bio;

  return (
    <div className="ring-foreground/10 overflow-hidden rounded-xl bg-card text-card-foreground ring-1 shadow-sm">
      <div className="h-28 bg-gradient-to-br from-muted via-accent/60 to-muted" />

      <div className="flex flex-col items-center -mt-12 px-6 pb-6">
        <Avatar className="h-24 w-24 ring-4 ring-card text-2xl">
          {profile.imageUrl && <AvatarImage src={profile.imageUrl} alt={displayName} />}
          <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
        </Avatar>

        <h1 className="mt-3 text-xl font-semibold tracking-tight text-foreground">{displayName}</h1>

        {profile.jobTitle && (
          <p className="mt-0.5 text-sm text-muted-foreground">{profile.jobTitle}</p>
        )}

        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground/80">
          <Mail className="h-3 w-3 shrink-0" />
          {profile.email}
        </p>
      </div>

      {hasAbout && (
        <div className="border-t border-border px-6 py-5">
          <h2 className="text-sm font-medium text-foreground">About</h2>

          {(profile.department || profile.location) && (
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
              {profile.department && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5 shrink-0" />
                  {profile.department}
                </span>
              )}
              {profile.location && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {profile.location}
                </span>
              )}
            </div>
          )}

          {profile.bio && (
            <p className="mt-3 text-sm leading-relaxed text-foreground/80 whitespace-pre-line">
              {profile.bio}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
